import React from "react";
import {
  Box,
  Typography,
  Stack,
  Alert,
} from "@mui/material";
import {
  Warning as WarningIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";
import { ModalV2 } from "@/components";
import { useDeleteProductsBatchMutation } from "@/services/product-catalogue/pc-api";

interface BatchDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
  productIds: string[];
  mode: "selected" | "all";
  totalProductCount?: number;
  clientId?: string;
}

const BatchDeleteDialog: React.FC<BatchDeleteDialogProps> = ({
  open,
  onClose,
  onDeleted,
  productIds,
  mode,
  totalProductCount,
  clientId,
}) => {
  const [deleteProductsBatch, { isLoading }] = useDeleteProductsBatchMutation();

  const handleDelete = async () => {
    try {
      await deleteProductsBatch({ ids: productIds, clientId }).unwrap();
      onDeleted();
      onClose();
    } catch (error) {
      console.error("Error deleting products:", error);
    }
  };

  const count = mode === "all" ? (totalProductCount ?? productIds.length) : productIds.length;

  return (
    <ModalV2
      show={open}
      setShow={onClose}
      title={mode === "all" ? "Clear Product Hub" : "Delete Selected Products"}
      subTitle="This action cannot be undone"
      buttonText={mode === "all" ? "Clear All Products" : `Delete ${count} Product${count !== 1 ? "s" : ""}`}
      buttonAction={handleDelete}
      isConfirmButtonLoading={isLoading}
      buttonLoader={false}
      maxWidth="sm"
      modalStyles={{
        "& .MuiPaper-root": {
          maxWidth: "600px",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Alert severity="error" sx={{ borderRadius: tokens.radius.lg }}>
            {mode === "all" ? (
              <Typography variant="body1">
                Are you sure you want to delete <strong>all {count} product{count !== 1 ? "s" : ""}</strong> from your Product Hub? This will permanently remove all products and their associated tariff calculations.
              </Typography>
            ) : (
              <Typography variant="body1">
                Are you sure you want to delete <strong>{count} selected product{count !== 1 ? "s" : ""}</strong>? This will permanently remove them and their associated tariff calculations.
              </Typography>
            )}
          </Alert>

          <Alert severity="warning" sx={{ borderRadius: tokens.radius.lg }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <WarningIcon sx={{ color: tokens.status.warning, fontSize: 20 }} />
              <Typography variant="body2" fontWeight={tokens.fontWeight.semibold}>
                {mode === "all"
                  ? "This will clear your entire Product Hub. This action is permanent and cannot be undone."
                  : "This action is permanent and cannot be undone. All associated tariff calculations will be lost."}
              </Typography>
            </Stack>
          </Alert>
        </Stack>
      </Box>
    </ModalV2>
  );
};

export default BatchDeleteDialog;
